import { parseFrontmatter, createImageResolver } from './markdown';

export interface ResearchPost {
  slug: string;
  title: string;
  date: string;
  order?: number;
  author: string;
  authorRole?: string;
  excerpt: string;
  image?: string;
  tags: string[];
  related?: string[];
  content: string;
}

// Load all markdown files from docs/research at build time
const researchFiles = import.meta.glob('../../../../docs/research/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
});

// Load all images from docs/research/assets at build time
const researchImages = import.meta.glob('../../../../docs/research/assets/*.{png,jpg,jpeg,webp,gif,svg}', {
  import: 'default',
  eager: true,
}) as Record<string, string>;

export const getResearchImageUrl = createImageResolver(researchImages);

function parseResearchPosts(): ResearchPost[] {
  const posts: ResearchPost[] = [];

  for (const [path, rawContent] of Object.entries(researchFiles)) {
    // Extract slug from filename
    const filename = path.split('/').pop();
    if (!filename) continue;
    const slug = filename.replace('.md', '');

    // Parse frontmatter and content
    const { data, content } = parseFrontmatter(rawContent as string);

    posts.push({
      slug,
      title: (data.title as string) || slug,
      date: data.date instanceof Date
        ? data.date.toISOString().split('T')[0]
        : String(data.date || ''),
      order: data.order != null ? Number(data.order) : undefined,
      author: (data.author as string) || 'Neumind Team',
      authorRole: data.authorRole as string | undefined,
      excerpt: (data.excerpt as string) || '',
      image: getResearchImageUrl(data.image as string | undefined),
      tags: Array.isArray(data.tags) ? data.tags : [],
      related: Array.isArray(data.related) ? data.related : undefined,
      content: content,
    });
  }

  return posts;
}

export const researchPosts: ResearchPost[] = parseResearchPosts();

export function getResearchPost(slug: string): ResearchPost | undefined {
  return researchPosts.find((post) => post.slug === slug);
}

export function getResearchPosts(): ResearchPost[] {
  return [...researchPosts].sort((a, b) => {
    if (a.order != null && b.order != null) return a.order - b.order;
    if (a.order != null) return -1;
    if (b.order != null) return 1;
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });
}

export function getResearchPostsByTag(tag: string): ResearchPost[] {
  return getResearchPosts().filter((post) =>
    post.tags.some((t) => t.toLowerCase() === tag.toLowerCase())
  );
}

